// frontend/src/context/SharingModeContext.js
import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';

const SHARING_MODE_KEY = 'sharingModeEnabled';

export const SharingModeContext = createContext();

// Custom hook for using the sharing mode context
export const useSharingMode = () => {
  const context = useContext(SharingModeContext);
  if (!context) {
    throw new Error('useSharingMode must be used within a SharingModeProvider');
  }
  return context;
};

export const SharingModeProvider = ({ children }) => {
  const [isSharingMode, setIsSharingMode] = useState(() => {
    // Initialize from localStorage
    const stored = localStorage.getItem(SHARING_MODE_KEY);
    return stored === 'true';
  });

  // Persist to localStorage when changed
  useEffect(() => {
    localStorage.setItem(SHARING_MODE_KEY, isSharingMode.toString());
  }, [isSharingMode]);

  // Toggle sharing mode
  const toggleSharingMode = useCallback(() => {
    setIsSharingMode(prev => !prev);
  }, []);

  // Mask a currency amount - returns masked string or original value
  const maskCurrency = useCallback((amount) => {
    if (!isSharingMode) return null; // Return null to indicate no masking
    return '$•••••';
  }, [isSharingMode]);

  // Mask a number (for percentages, counts, etc.)
  const maskNumber = useCallback((number) => {
    if (!isSharingMode) return null;
    return '•••';
  }, [isSharingMode]);

  // Mask account name
  const maskAccountName = useCallback((name) => {
    if (!isSharingMode) return name;
    if (!name) return name;
    // Keep first letter and mask the rest
    return name.charAt(0) + '••••••••';
  }, [isSharingMode]);

  // Mask merchant/transaction name
  const maskMerchantName = useCallback((name) => {
    if (!isSharingMode) return name;
    if (!name) return name;
    // Replace with generic merchant
    return 'Merchant';
  }, [isSharingMode]);

  // Mask account number/mask (the last 4 digits)
  const maskAccountNumber = useCallback((accountMask) => {
    if (!isSharingMode) return accountMask;
    return '••••';
  }, [isSharingMode]);

  return (
    <SharingModeContext.Provider
      value={{
        isSharingMode,
        setIsSharingMode,
        toggleSharingMode,
        maskCurrency,
        maskNumber,
        maskAccountName,
        maskMerchantName,
        maskAccountNumber
      }}
    >
      {children}
    </SharingModeContext.Provider>
  );
};
